import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ERROR_CODES, BlendEvent, ErrCode, PermissionActions, User } from '../../models';
import { ApiService } from 'src/app/core/api.service';
import { UserService } from 'src/app/core/user.service';
import { Alert, UtilService } from '@material-library';
import { NavigationEnd, Router } from '@angular/router';
import { BillingService } from 'src/app/settings/billing/billing.service';
import { Subscription, filter } from 'rxjs';
import { BlendService } from '../../services/blend.service';
import { CAccountInfo } from '../../models/api/chargbeeprofile-api.model';
import { ChargebeeService } from '../../services/chargebee.service';
import { MultiAdminService } from 'src/app/core/multiadmin.service';
import { HELP_LINKS } from '../../shared.constants';

@Component({
  selector: 'app-global-message',
  templateUrl: './global-message.component.html',
  styleUrls: ['./global-message.component.scss'],
})
export class GlobalMessageComponent implements OnInit, OnDestroy, OnChanges {
  public trackByAlertKey = (_index: number, alert: { key: string }): string => alert.key;

  @Input() public user: User;
  public errcode: ErrCode;
  public success = false;
  public alertsConfig: { [key: string]: Alert } = {};
  public cprofile: CAccountInfo;
  public planExpiry: string;
  public paidThru: string;
  private chargebeeSubscription: Subscription;
  private currentUsers: number;
  private userLimit: number;
  private THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
  private routerSubscription: Subscription;
  private errorSubscription: Subscription;

  constructor(
    private apiService: ApiService,
    private userService: UserService,
    private chargebeeService: ChargebeeService,
    private utilService: UtilService,
    private router: Router,
    private billingService: BillingService,
    private multiadminService: MultiAdminService,
    private blendService: BlendService,
  ) {}

  public ngOnInit(): void {
    this.errcode = undefined;
    this.errorSubscription = this.apiService.error$.subscribe((err) => {
      this.updateBlockedTrafficAlert(err);
    });
    this.chargeBeeLoadSubscription();
    this.initAlerts();
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkStorageUsageBanner();
        const err = this.apiService.currentError;
        this.updateBlockedTrafficAlert(err);
      });
  }

  public ngOnDestroy(): void {
    this.chargebeeSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.errorSubscription?.unsubscribe();
  }

  public ngOnChanges(): void {
    this.alertsConfig = {};
    this.initAlerts();
    this.checkStorageUsageBanner();
  }

  public initAlerts(): void {
    if (!this.user.is_verified) {
      this.alertsConfig.verifyEmailAlert = {
        type: 'warning',
        showLeadIcon: true,
        leadIcon: 'info',
        title: 'Your email address has not been verified.',
        description: 'Confirm your email address by following the instructions in the verification email.',
        showActions: true,
        showPrimaryAction: true,
        showCloseButton: false,
        showSecondaryAction: false,
        secondaryButtonConfig: {
          label: 'Cancel',
        },
        primaryButtonConfig: {
          label: 'Re-send verification email',
          onClick: () => this.resendVerification(),
        },
      };
    }
  }

  public resendVerification = async (): Promise<void> => {
    try {
      await this.apiService.execute('userverifyemailresend', {});
      this.success = true;
      this.alertsConfig.verifyEmailAlert.title = `You should receive an email at ${this.userService.get('email')} soon.`;
      this.alertsConfig.verifyEmailAlert.description = '';
      this.alertsConfig.verifyEmailAlert.showPrimaryAction = false;
    } catch (ex) {
      this.errcode = ErrCode.fromException(ex);
    }
  };

  private chargeBeeLoadSubscription(): void {
    const hasBillingPermission =
      this.user.is_multi_admin || this.userService.checkPermission(PermissionActions.MANAGE_BILLING);
    this.chargebeeSubscription = this.chargebeeService.getSubscription().subscribe((data) => {
      if (data.isLoaded) {
        this.cprofile = data.cprofile;
        if (this.cprofile && this.cprofile?.subscription && this.cprofile?.subscription?.subscriptionEndDate) {
          this.planExpiry = this.utilService.formatDate(this.cprofile.subscription.subscriptionEndDate, 'MMMM d, y');

          // Show Renew Subscription alert if the subscription is non-renewing and within the last 30 days
          const endDate = new Date(this.cprofile.subscription.subscriptionEndDate);
          const timeDifference = endDate.getTime() - Date.now();
          const isWithinLast30Days = timeDifference <= this.THIRTY_DAYS;
          if (this.cprofile.subscription?.status === 'non_renewing' && hasBillingPermission && isWithinLast30Days) {
            this.showCBRenewSubscription();
          }
        }
        if (hasBillingPermission && this.user.is_multi_new && this.cprofile?.subscription?.user_limit) {
          this.userLimit = this.cprofile?.subscription?.user_limit;
          this.reloadData();
        }
      }
    });
  }

  private showUnpaidInvoices(): void {
    this.alertsConfig.unpaidInvoice = {
      type: 'critical',
      showLeadIcon: true,
      leadIcon: 'error',
      title: 'You have an unpaid invoice.',
      description: `Your plan is paid until ${this.paidThru}. Your account may be suspended after this date.`,
      showActions: true,
      showCloseButton: false,
      showSecondaryAction: false,
      secondaryButtonConfig: {
        label: 'Cancel',
      },
      showPrimaryAction: true,
      primaryButtonConfig: {
        label: 'Pay now',
        onClick: this.onPayNow,
      },
    };
  }

  private checkStorageUsageBanner(): void {
    // temporary disabled
    delete this.alertsConfig.storageUsageBanner;
    return;

    const path = this.router.url.replace(/^\/4\.0/, '');

    const shouldShowStorageBanner =
      path.startsWith('/files') ||
      path.startsWith('/vault') ||
      path.startsWith('/recents') ||
      path.startsWith('/starred');

    if (!this.user || !shouldShowStorageBanner || this.user.is_unlimited_plan) {
      delete this.alertsConfig.storageUsageBanner;
      return;
    }

    const isChildUser = this.user.is_multi_child;
    const usagePercent = this.user.disk_usage_percent;

    // 1. Over quota: disk usage in bytes >= limit
    if (this.user.disk_usage_bytes >= this.user.disk_limit_bytes) {
      this.alertsConfig.storageUsageBanner = {
        type: 'critical',
        showLeadIcon: true,
        leadIcon: 'error',
        title: 'Your storage is full',
        description: isChildUser
          ? 'Please delete files to free up storage or reach out to your Admin to request an upgrade.'
          : 'Please upgrade or delete files to free up more storage.',
        showActions: true,
        showCloseButton: false,
        showSecondaryAction: false,
        secondaryButtonConfig: { label: '' },
        showPrimaryAction: !isChildUser,
        primaryButtonConfig: {
          label: 'Upgrade',
          onClick: (): void => this.onUpgradePlan('Upgrade (storage full banner)'),
        },
      };

      // 2. Warning: disk usage percent between 80% and just under 100%
    } else if (usagePercent >= 80 && usagePercent < 100) {
      this.alertsConfig.storageUsageBanner = {
        type: 'warning',
        showLeadIcon: true,
        leadIcon: 'info',
        title: `You're almost out of storage`,
        description: isChildUser
          ? `You're nearing your storage limit. To avoid interruptions, consider deleting unused files or contacting your Admin to request an upgrade.`
          : `You're nearing your storage limit. To avoid interruptions, you can delete unused files or upgrade your storage at any time.`,
        showActions: true,
        showCloseButton: false,
        showSecondaryAction: false,
        secondaryButtonConfig: { label: '' },
        showPrimaryAction: !isChildUser,
        primaryButtonConfig: {
          label: 'Upgrade',
          onClick: (): void => this.onUpgradePlan('Upgrade (nearing storage limit banner)'),
        },
      };
    } else {
      delete this.alertsConfig.storageUsageBanner;
    }
  }

  private upgradeSeats = (): void => {
    this.router.navigate(['/settings/upgrade-plan']);
  };
  private onGotoSettings = (): void => {
    this.router.navigate(['/settings/billing']);
  };

  private onPayNow = (): void => {
    this.router.navigate(['/account/renew']);
  };

  private onUpgradePlan = (trackingLabel = 'Upgrade (storage full banner)'): void => {
    this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: trackingLabel });
    this.router.navigate(['/account/upgrade']);
  };

  private async reloadData(): Promise<void> {
    const result = await this.multiadminService.getUserList();
    if (result.users.length > this.userLimit) {
      this.showAlertUserLimitExceeded();
    }
  }
  private showAlertUserLimitExceeded(): void {
    this.alertsConfig.AlertUserLimitExceeded = {
      type: 'critical',
      showLeadIcon: true,
      leadIcon: 'info',
      title: `You have too many users`,
      description: `Please upgrade or remove users.`,
      showActions: true,
      showCloseButton: false,
      showSecondaryAction: false,
      secondaryButtonConfig: {
        label: 'Cancel',
      },
      showPrimaryAction: true,
      primaryButtonConfig: {
        label: 'Add Users',
        onClick: this.upgradeSeats,
      },
    };
  }

  private onReactivateClick = (): void => {
    this.chargebeeService.gotoHubspotUpgradePage();
  };

  private showCBRenewSubscription(): void {
    this.alertsConfig.unpaidInvoice = {
      type: 'critical',
      showLeadIcon: true,
      leadIcon: 'error',
      title: 'Your subscription will be cancelled soon',
      description: `You\'ll retain premium features until ${this.planExpiry}. Reactivate now to avoid service interruption.`,
      showActions: true,
      showCloseButton: false,
      showSecondaryAction: false,
      secondaryButtonConfig: {
        label: 'Cancel',
      },
      showPrimaryAction: true,
      primaryButtonConfig: {
        label: 'Reactivate Now',
        onClick: this.onReactivateClick,
      },
    };
  }

  private updateBlockedTrafficAlert(errcode): void {
    if (errcode?.code === ERROR_CODES.BOT_PROTECTION_BLOCKED_TRAFFIC) {
      this.alertsConfig.blockedTraffic = {
        type: 'critical',
        showLeadIcon: true,
        leadIcon: 'error',
        title: '',
        description: `Your request was blocked by our Web Application Firewall (WAF). If you believe this is an error, please <a class="help-link" href="${HELP_LINKS.CLOUDFLARE_HELP_URL}" target="_blank">visit our help page</a>.`,
        showActions: true,
        showCloseButton: false,
        showSecondaryAction: false,
        secondaryButtonConfig: { label: '' },
        showPrimaryAction: false,
        primaryButtonConfig: {
          label: '',
        },
      };
    } else {
      delete this.alertsConfig.blockedTraffic;
    }
  }
}
